/**
 * Contract template upload & field configuration (PE-2145).
 *
 * Mirrors osprey-api's contract-templates module DTOs, which in turn mirror
 * watchman-service's Prisma models (osprey-api does not own this data — it proxies
 * watchman). TCS-staff only; the FE surface here drives create/version/activate +
 * Required/Optional field configuration against a merchant onboarding contract.
 *
 * Legal-role editing (PE-2147): the page's edit gate is the TCS-admin
 * CONTRACT_TEMPLATE_MANAGE role permission OR a JWT claim of
 * MERCHANT_ONBOARDING:MANAGE — the resource osprey-api's controller enforces —
 * which a department role (e.g. Legal) can grant via /departments.
 */

// NB: the project's build enables `erasableSyntaxOnly`, so TS `enum`s are disallowed.
// These mirror the backend enums using the codebase's const-object + union pattern
// (same as `Permission` / `BOOKING_MEDIUMS`).

/** Mirrors osprey-api `MerchantContractType` (merchant-contract-terms-response.dto.ts). */
export const MerchantContractType = {
  COMMISSION: 'COMMISSION',
  OUTRIGHT: 'OUTRIGHT',
  OUTRIGHT_INSTALMENT: 'OUTRIGHT_INSTALMENT',
} as const;
export type MerchantContractType = (typeof MerchantContractType)[keyof typeof MerchantContractType];

export const CONTRACT_TYPE_LABELS: Record<MerchantContractType, string> = {
  [MerchantContractType.COMMISSION]: 'Commission',
  [MerchantContractType.OUTRIGHT]: 'Outright',
  [MerchantContractType.OUTRIGHT_INSTALMENT]: 'Outright (instalment)',
};

/** Mirrors watchman's `ContractFieldRequirement` enum. */
export const ContractFieldRequirement = {
  REQUIRED: 'REQUIRED',
  OPTIONAL: 'OPTIONAL',
} as const;
export type ContractFieldRequirement =
  (typeof ContractFieldRequirement)[keyof typeof ContractFieldRequirement];

/** Mirrors watchman's `ContractTemplateFieldKey` enum. */
export const ContractTemplateFieldKey = {
  MERCHANT_NAME: 'MERCHANT_NAME',
  COMPANY_ADDRESS: 'COMPANY_ADDRESS',
  COMMISSION_RATE: 'COMMISSION_RATE',
  CONTRACT_TYPE: 'CONTRACT_TYPE',
  CONTRACT_START_DATE: 'CONTRACT_START_DATE',
  CONTRACT_END_DATE: 'CONTRACT_END_DATE',
  NEXT_CONTRACT_REVIEW_DATE: 'NEXT_CONTRACT_REVIEW_DATE',
  INVOICING_CADENCE: 'INVOICING_CADENCE',
} as const;
export type ContractTemplateFieldKey =
  (typeof ContractTemplateFieldKey)[keyof typeof ContractTemplateFieldKey];

/** Human labels + the order the eight keys render in the field-config editor. */
export const CONTRACT_FIELD_KEYS: { key: ContractTemplateFieldKey; label: string }[] = [
  { key: ContractTemplateFieldKey.MERCHANT_NAME, label: 'Merchant name' },
  { key: ContractTemplateFieldKey.COMPANY_ADDRESS, label: 'Company address' },
  { key: ContractTemplateFieldKey.COMMISSION_RATE, label: 'Commission rate' },
  { key: ContractTemplateFieldKey.CONTRACT_TYPE, label: 'Contract type' },
  { key: ContractTemplateFieldKey.CONTRACT_START_DATE, label: 'Contract start date' },
  { key: ContractTemplateFieldKey.CONTRACT_END_DATE, label: 'Contract end date' },
  { key: ContractTemplateFieldKey.NEXT_CONTRACT_REVIEW_DATE, label: 'Next contract review date' },
  { key: ContractTemplateFieldKey.INVOICING_CADENCE, label: 'Invoicing cadence' },
];

export interface ContractTemplateField {
  id: string;
  fieldKey: ContractTemplateFieldKey;
  label: string;
  requirement: ContractFieldRequirement;
}

export interface ContractTemplate {
  id: string;
  name: string;
  version: number;
  contentHtml: string;
  documentUrl: string | null;
  contractType: MerchantContractType | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  fields?: ContractTemplateField[];
}

export interface CreateContractTemplatePayload {
  name: string;
  contentHtml: string;
  documentUrl?: string;
  contractType?: MerchantContractType;
}

export interface CreateContractTemplateVersionPayload {
  contentHtml: string;
  documentUrl?: string;
  contractType?: MerchantContractType;
}

export interface ContractTemplateFieldInput {
  fieldKey: ContractTemplateFieldKey;
  label: string;
  requirement: ContractFieldRequirement;
}

export interface SetContractTemplateFieldsPayload {
  fields: ContractTemplateFieldInput[];
}

/**
 * Tenant key the contract-template source documents are uploaded under.
 *
 * Contract templates are TCS *platform* artefacts, not a merchant's: every admin
 * must see and version the same corpus. `useMediaUpload` otherwise falls back to
 * the auth store's `selectedTenantId`, which is (a) empty for a platform-level
 * TCS admin with no merchant selected — media-service then 400s
 * "x-tenant-id header required", surfacing through osprey-api as a 502 (PE-2286)
 * — and (b) wrong when a merchant *is* selected, since it would scatter identical
 * platform documents under whichever merchant the tenant switcher happened to be
 * on. media-service treats x-tenant-id as a free-form string (the RLS scope and
 * the `<solutionId>/<tenantId>/` GCS object-key prefix, with no FK to a merchant),
 * so a fixed platform key is both valid and the semantically correct scope.
 */
export const CONTRACT_TEMPLATE_TENANT_ID = 'tcs-platform';
